<!-- DocumentList.vue -->
<script lang="ts" setup>
import { TitleType, ButtonDisplayStyle, DisplayStyle, IconTypes, CssFramework, HoverCursorTypes, ExtraButtonDisplayStyle, TextDisplayStyle, ButtonDisplaySize } from '../../Enums/UiEnums';
import { ActionButtonStyle } from '../../lib/Types/Types';
import { popAlert, showToast } from '../../lib/Notification';
import { isBetweenDates, prettyDateTime } from '../../lib/DateUtils';
import { computed, nextTick, onBeforeMount, reactive, ref, watch } from 'vue';
import { initPinia } from '../../lib/PiniaMgr';
import { useSurescriptsRecordLocatorStore } from '../../lib/Stores/SurescriptsRecordLocatorStore';
import { useRouter } from 'vue-router';
import { ChoiceApi } from '../../LibMain';
import { ISelection } from '../../lib/Models/Selection';
import { assertInternally, serverEnvironment } from '../../lib/Environment';
import { containsAll } from "../../lib/Matching";
import { DatabaseIdTypes, EnvironmentType, MenuNameType } from '../../Enums/ChoiceEnums';
import { fileExtFromMimeType } from '../../lib/FileUtils';
import { timeout } from '../../lib/ChoiceUtils';
import _ from 'lodash';
import ActionButtonV2 from '../../components/ActionButtonV2.vue';
import AlertSection from '../../components/Messages/AlertSection.vue';
import Card from '../../components/Card.vue';
import Checkbox from '../../components/Inputs/Checkbox.vue';
import Icon from '../../components/Icon.vue';
import Row from '../../components/Section/Row.vue';
import Title from '../../components/Section/Title.vue';
import RetrieveStatusBadge from "./RetrieveStatusBadge.vue";
import TextInput from '../../components/TextInput.vue';
import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
import DropDownButton from '../../components/DropDownButton.vue';
import DropDownButtonItem from '../../components/DropDownButtonItem.vue';
import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
import MessageBadge from '../../components/Messages/MessageBadge.vue';
import EDocumentSaveScreen from '../EDocuments/EDocumentSaveScreen.vue';
import VerticalSpacer from '../../components/Section/VerticalSpacer.vue';
import GeneralDialog from '../../components/GeneralDialog.vue';
import MessageText from '../../components/Messages/MessageText.vue';
import ToggleIcon from '../../components/ToggleIcon.vue';
import DocumentViewer from './DocumentViewer.vue';
import { getImportErrorMsg, IDocumentListItem, isCCDDocument, documentTitle, docFileNameWithoutExtension } from './Models/IDocumentListItem';
import { DocumentRetrieveStatus } from './Models/DocumentRetrieveStatus';
import SharingHistoryDialog from './SharingHistoryDialog.vue';
import { CssLayoutUnits, CssLength } from '../../lib/CssHelpers';

    const surescriptsRecordLocatorStore = useSurescriptsRecordLocatorStore(initPinia());
    
    const router = useRouter();

    const props = defineProps<{
        patientId: number
    }>();

    enum FilterListBy
    {
        Title = "Title",
        Type = "Type",
        Category = "Category",
        Source = "Source",
        Author = "Author",
        //Encounter = "Encounter"
    };

    enum SortListBy
    {
        NewestFirst,
        OldestFirst,
        Type,
        Category,
        Source,
        Author,
        Title
    };    

    const SortByNames: Record<SortListBy, string> = {
        [SortListBy.NewestFirst]: "Newest First",
        [SortListBy.OldestFirst]: "Oldest First",
        [SortListBy.Type]: "Type",
        [SortListBy.Category]: "Category",
        [SortListBy.Source]: "Source",
        [SortListBy.Author]: "Author",
        [SortListBy.Title]: "Title"
    }

    type IDocumentListItemAndClientSideData = IDocumentListItem & ISelection & { savedToIdType?: DatabaseIdTypes, savedToId?: number, saveErrorMsg?: string };

    const compData = reactive<{
        selectAllRecords?: boolean,
        documents?: (IDocumentListItemAndClientSideData & {hideCardDetails?: boolean})[],
        cachedDocumentsDateUTC?: Date,
        sortBy?: SortListBy,
        filterText?: string,
        filterBy?: FilterListBy,
        encounterFilterStartDate?: string,
        encounterFilterEndDate?: string,
        patientEntityId?: number,
        showEDocumentSaveScreen?: boolean,
        documentToImport?: IDocumentToImport,
        originalDocumentRetrieveStatus?: DocumentRetrieveStatus,

        showDocumentViewer?: boolean,
        documentToView?: IDocumentListItem,
        loadingDocumentToView?: boolean,
        documentToViewBlobUrl?: string,

        showSharingHistoryDialog?: boolean
    }>({
        sortBy: SortListBy.NewestFirst,
        filterBy: FilterListBy.Title
    });

    interface IDocumentToImport {documentUrl: string, patientEntityId: number, fileExt: string|undefined, doc: IDocumentListItemAndClientSideData};

    const saveEDocsRef = ref<InstanceType<typeof EDocumentSaveScreen>>();
    const documentViewerDialogWidth = new CssLength(75, CssLayoutUnits.vw);        
            
    onBeforeMount(() =>{
        // Get the patient entity ID
        ChoiceApi.entityInfo.getEntityInfoByDatabaseId({ idType: DatabaseIdTypes.patient_id, id: props.patientId})
            .then(resp =>{
                compData.patientEntityId = resp.entityId;
            });

        const cachedDocs = surescriptsRecordLocatorStore.documentsList;
        if (undefined === cachedDocs)
        {            
            navigateToSearchPage();
            return;
        }

        compData.documents = cachedDocs;
        
        compData.cachedDocumentsDateUTC = surescriptsRecordLocatorStore.cachedDocumentsDateUTC;

        updateDocumentStatusInfo();
    });

    watch(() => props.patientId, (newValue, oldValue) => { 
        if (newValue !== oldValue) { 
            surescriptsRecordLocatorStore.clearDocumentList();

            // If we have a valid patient ID, navigate back to the root page
            if (newValue)
            {
                navigateToSearchPage();
            }
        }
    });

    async function updateDocumentStatusInfo(): Promise<void>
    {
        ChoiceApi.surescriptsRLE.getPatientDocumentStatusInfo(props.patientId).then(resp => {

            // Update the documents with status info
            for(const docStatus of resp ?? [])
            {
                const matchingDoc = compData.documents?.find(doc => doc.url?.endsWith(docStatus.surescriptsDocumentUrl));
                if (matchingDoc)
                {
                    matchingDoc.retrieveStatus = docStatus.documentRetrieveStatus;
                    matchingDoc.saveErrorMsg = docStatus.saveErrorMsg;
                    matchingDoc.savedToIdType = docStatus.savedToIdType;
                    matchingDoc.savedToId = docStatus.savedToId;                    
                }
            }

            // Update any documents without a status to 'Available'
            for(const doc of compData.documents ?? [])
            {
                if (undefined == doc.retrieveStatus)
                    doc.retrieveStatus = DocumentRetrieveStatus.Available;
            }
        });
    }
    
    function documentResultCountText()
    {
        const count = compData.documents?.length ?? 0;
        if (0 == count)
            return "No results";
        if (1 == count)
            return "1 result";
        return `${count} results`
    }

    async function refreshData() 
    {
        // Tell the server to clear the cache
        const deleteResp = await ChoiceApi.surescriptsRLE.deleteCachedDocuments(props.patientId);
        if (!deleteResp)
        {            
            await popAlert("Unable to delete cached documents");
        }

        // Now clear the local data
        surescriptsRecordLocatorStore.clearDocumentList();
        
        navigateToSearchPage();    
    }

    function navigateToSearchPage()
    {            
        router.push({name: "Search"});
    }

    function onChangeSelectAllRecords(value:boolean)
    {
        compData.documents?.forEach(doc => doc.selected = value);
    }

    const selectAllRecordsText = computed(() => compData.selectAllRecords ? "Deselect All Records" : "Select All Records")

    async function onViewDocument(doc: IDocumentListItem)
    {
        compData.originalDocumentRetrieveStatus = doc.retrieveStatus;
        compData.documentToView = doc;
        compData.showDocumentViewer = true;
        compData.documentToViewBlobUrl = undefined;
        compData.loadingDocumentToView = true;

        // Update status if we've not gotten the document yet
        switch(doc.retrieveStatus)
        {
            case DocumentRetrieveStatus.Available:
            case DocumentRetrieveStatus.Error:
            case undefined:
            updateDocumentRetrieveStatus(DocumentRetrieveStatus.Retrieving, doc);
            break;
        }
    }

    async function onClickImportDocument(doc:IDocumentListItem)
    {
        const statusError = getImportErrorMsg(doc);
        if (statusError)
        {
            await popAlert(`${statusError}: ${documentTitle(doc)}`);
            return;
        }
        
        compData.showDocumentViewer = false;
        if (isCCDDocument(doc))
        {
            await onImportCCD(doc);
        }
        else
        {
            await onStartImportEDocument(doc);
        }
    }

    async function onStartImportEDocument(doc:IDocumentListItem) 
    {
        compData.documentToImport = await verifyDocumentCanBeImported(doc);

        if (!compData.documentToImport)
            return;

        compData.showEDocumentSaveScreen = true;
    }

    function closeEDocumentsSaveScreen()
    {
        //compData.showEDocumentSaveScreen = false;
        if (compData.documentToImport)
        {            
            compData.documentToImport.doc.retrieveStatus = compData.originalDocumentRetrieveStatus ?? DocumentRetrieveStatus.Available;
        }
    }

    // Save an eDocument and return after the save is complete
    async function importEDocument(doc: IDocumentListItem): Promise<void>
    {
        return new Promise(async (resolve, _reject) => {
            await onStartImportEDocument(doc);

            // Periodically check if the save window has closed
            let checkingIfDisplayed = true;
            while(checkingIfDisplayed)
            {
                await timeout(100);
                if (!compData.showEDocumentSaveScreen)
                {
                    checkingIfDisplayed = false;
                }
            }

            // End the promise
            nextTick().then(() => {
                resolve();
            });
        });
    }

    async function completeImportDocument()
    {
        const documentToImport = compData.documentToImport;
        if (!documentToImport)
        {
            await popAlert("compData.documentToImport is undefined");
            return;
        }

        // Is the EDocument info valid?
        const eDocInfo = saveEDocsRef.value?.getSaveInfo();
        if (!eDocInfo)
            return;

        // Close the EDocuments screen
        compData.showEDocumentSaveScreen = false;

        // Get the matching document
        const doc = compData.documents?.find(d => d.url === documentToImport.doc.url);
        if (!doc)
        {
            await popAlert("Unable to get document info");
            return;
        }
        
        if (!doc.url)
        {
            await popAlert("Import failed: DocumentId URL invalid");
            return;
        }

        // Make sure to perform in nextTick or screen may not re-paint to show "Importing"
        nextTick(() => updateDocumentRetrieveStatus(DocumentRetrieveStatus.Importing, doc));
        doc.saveErrorMsg = undefined;

        var resp = await ChoiceApi.surescriptsRLE.importEDocument(
            doc.url, 
            props.patientId, 
            { 
                documentName: eDocInfo.filename,
                folderId: eDocInfo.folderMenuId,
                receivedDate: eDocInfo.receivedDate,
                reviewInfo: 
                {
                    message: eDocInfo.reviewMessage,
                    priority: eDocInfo.priority,
                    reviewers: eDocInfo.reviewList}
            });

        if (!resp?.success)
        {
            doc.retrieveStatus = DocumentRetrieveStatus.Error;
            doc.saveErrorMsg = resp?.message;
            showToast(`Failed to import document: ${resp?.message ?? "Unknown error"}`, DisplayStyle.Danger);
            return;
        }
        if (!resp.data)
        {
            doc.retrieveStatus = DocumentRetrieveStatus.Error;
            showToast("Failed to import document: Missing database ID", DisplayStyle.Danger);
            return;            
        }

        doc.retrieveStatus = DocumentRetrieveStatus.Imported;
    }

    async function onImportCCD(doc: IDocumentListItemAndClientSideData)
    {
        updateDocumentRetrieveStatus(DocumentRetrieveStatus.Importing, doc);
        doc.saveErrorMsg = undefined;

        if (undefined === doc.url)
        {
            doc.retrieveStatus = DocumentRetrieveStatus.Error;
            showToast("No document URL was availble.  Unable to fetch from Surescripts", DisplayStyle.Danger);
            return;
        }

        const resp = await ChoiceApi.surescriptsRLE.importCCD(doc.url, props.patientId);
        if (!resp?.success)
        {
            doc.retrieveStatus = DocumentRetrieveStatus.Error;
            doc.saveErrorMsg = resp?.message;
            showToast(`Failed to import CCD: ${resp?.message ?? "Unknown error"}`, DisplayStyle.Danger);
            return;
        }
        if (!resp.data)
        {
            doc.retrieveStatus = DocumentRetrieveStatus.Error;
            showToast("Failed to import CCD: Missing database ID", DisplayStyle.Danger);
            return;            
        }
        
        doc.savedToId = +resp.data.id;
        doc.savedToIdType = resp.data.idType;
        doc.retrieveStatus = DocumentRetrieveStatus.Imported;
    }

    async function verifyDocumentCanBeImported(doc:IDocumentListItem): Promise<IDocumentToImport|undefined>{
        if (!compData.patientEntityId)
        {
            await popAlert('patientEntityId is undefined.');
            return;
        }

        if (!doc.url)
        {
            await popAlert('Document URL is undefined.');
            return;
        } 

        if (!doc.contentType)
        {
            await popAlert('ContentType is undefined.');
            return;
        }

        const fileExt = fileExtFromMimeType(doc.contentType);

        return  {documentUrl: doc.url, patientEntityId: compData.patientEntityId, fileExt: fileExt, doc: doc };
    }

    function onClickSortDropDownItem(value: SortListBy)
    {
        compData.sortBy = value;
    }

    function onClickFilterByDropDownItem(value: FilterListBy)
    {
        compData.filterBy = value;
    }

    const visibleAndSortedDocumentItems = computed(() => {
        let visibleDocs = compData.documents;

        // Filter by encounter date
        if (compData.encounterFilterStartDate || compData.encounterFilterEndDate)
        {
            const encounterFilterStartDateUTC = compData.encounterFilterStartDate ? new Date(compData.encounterFilterStartDate) : undefined;
            const encounterFilterEndDateUTC = compData.encounterFilterEndDate ? new Date(compData.encounterFilterEndDate) : undefined;

            visibleDocs = visibleDocs?.filter(doc => {
                if (isBetweenDates(doc.startTimestampUTC, encounterFilterStartDateUTC, encounterFilterEndDateUTC) || isBetweenDates(doc.endTimestampUTC, encounterFilterStartDateUTC, encounterFilterEndDateUTC))
                {
                    return true;
                }
            });
        }

        if (compData.filterText?.trim()?.length)
        {
            const matchParts = compData.filterText.toLowerCase().split(" ");

            switch(compData.filterBy)
            {
                case FilterListBy.Title:
                //visibleDocs = visibleDocs?.filter(doc => containsAll(doc.description?.toLowerCase(), matchParts));
                visibleDocs = visibleDocs?.filter(doc => containsAll(documentTitle(doc)?.toLowerCase(), matchParts));
                break;

                case FilterListBy.Type:
                visibleDocs = visibleDocs?.filter(doc => containsAll(doc.type?.displayName?.toLowerCase(), matchParts));
                break;

                case FilterListBy.Category:
                visibleDocs = visibleDocs?.filter(doc => containsAll(doc.category?.displayName?.toLowerCase(), matchParts));
                break;

                case FilterListBy.Source:
                visibleDocs = visibleDocs?.filter(doc => containsAll(doc.source?.toLowerCase(), matchParts));
                break;

                case FilterListBy.Author:
                visibleDocs = visibleDocs?.filter(doc => containsAll(doc.author?.toLowerCase(), matchParts));
                break;

                default:
                    assertInternally(`Unhandled filterBy value: ${compData.filterBy}`);
                    break;
            }
        }

        const lastAlphabeticSortValue = "zzzzzzzzzzzzzzzz";

        switch (compData.sortBy)
        {
            case SortListBy.NewestFirst:
                return _.orderBy(visibleDocs, doc => doc.startTimestampUTC ?? "1900-01-01", ["desc"]);
                
            case SortListBy.OldestFirst:
                return _.orderBy(visibleDocs, doc => doc.startTimestampUTC ?? "2200-01-01");

            case SortListBy.Type:
                return _.orderBy(visibleDocs, doc => doc.type?.displayName ?? lastAlphabeticSortValue);

            case SortListBy.Category:
                return _.orderBy(visibleDocs, doc => doc.category?.displayName ?? lastAlphabeticSortValue);

            case SortListBy.Source:
                return _.orderBy(visibleDocs, doc => doc.source ?? lastAlphabeticSortValue);

            case SortListBy.Author:
                return _.orderBy(visibleDocs, doc => doc.author ?? lastAlphabeticSortValue);

            case SortListBy.Title:
                return _.orderBy(visibleDocs, doc => doc.description ?? lastAlphabeticSortValue);

            undefined:
                return visibleDocs;
            
            default:
                assertInternally(`Bad sort case value: ${compData.sortBy}`);
        }
    });

    function resetFilters()
    {
        compData.filterText = undefined;
        compData.encounterFilterStartDate = undefined;
        compData.encounterFilterEndDate = undefined;
    }

    const showResetFilterButton = computed(() => {
        if ((compData.filterText?.trim().length ?? 0) > 0)
            return true;
        if (compData.encounterFilterStartDate || compData.encounterFilterEndDate)
            return true;
        return false;
    });

    const selectedVisibleDocuments = computed<IDocumentListItemAndClientSideData []>(() => 
        visibleAndSortedDocumentItems.value?.filter(doc => doc.selected) ?? []
    );

    async function importSelected()
    {
        const importableDocuments = selectedVisibleDocuments.value?.filter(doc => 
                (DocumentRetrieveStatus.Available === doc.retrieveStatus || DocumentRetrieveStatus.Error === doc.retrieveStatus));

        // Start importing the CCDs
        const selectedCCDs = importableDocuments?.filter(isCCDDocument);
        for (const doc of selectedCCDs ?? [])
        {
            onImportCCD(doc);
        }
        
        // Import the eDocuments.
        const selectedEDocs = importableDocuments?.filter(doc => !isCCDDocument(doc));
        for (const doc of selectedEDocs ?? [])
        {
            await importEDocument(doc);
        }
    }

    function cancelImportSelections()
    {
        onChangeSelectAllRecords(false); 
        compData.selectAllRecords = false;
    }

    function setFilter(filterBy: FilterListBy, filterText?: string): void
    {
        compData.filterBy = filterBy; 
        compData.filterText = filterText;
    }

    function matchesFilter(filterBy: FilterListBy, filterText?: string): boolean
    {
        return compData.filterBy === filterBy && compData.filterText === filterText;
    }

    function showAllDocumentDetails(showDetails: boolean)
    {
        for(const doc of compData.documents ?? [])
        {
            doc.hideCardDetails = !showDetails;
        }
    }

    const isDevEnv = computed(()=> serverEnvironment() === EnvironmentType.Dev);

    function documentViewerLoadComplete()
    {
        compData.loadingDocumentToView = false;
        if (compData.documentToView)
        {
            if (DocumentRetrieveStatus.Available == compData.originalDocumentRetrieveStatus || DocumentRetrieveStatus.Error == compData.originalDocumentRetrieveStatus)
            {
                compData.documentToView.retrieveStatus = DocumentRetrieveStatus.Retrieved;    
            }
            else
            {
                compData.documentToView.retrieveStatus = compData.originalDocumentRetrieveStatus ?? DocumentRetrieveStatus.Retrieved;
            }
        }
    }

    function errorLoadingDocument()
    {
        compData.loadingDocumentToView = false;
        compData.showDocumentViewer = false;
        if (compData.documentToView)
        {
            compData.documentToView.retrieveStatus = DocumentRetrieveStatus.Error;            
        }
        nextTick(() => popAlert("Error loading document"));
    }

    function updateDocumentRetrieveStatus(newStatus: DocumentRetrieveStatus, doc: IDocumentListItem)
    {
        compData.originalDocumentRetrieveStatus = doc.retrieveStatus;
        doc.retrieveStatus = newStatus;
    }

</script>


 <template>
    <AlertSection v-if="undefined === compData.documents" :displayStyle="DisplayStyle.Danger">
        documents property is undefined
    </AlertSection>
    <AlertSection v-if="undefined === compData.cachedDocumentsDateUTC" :displayStyle="DisplayStyle.Danger">
        cachedDocumentsDateUTC property is undefined
    </AlertSection>    

    <Row>
        <template #left>
            <Title :type="TitleType.PageBreadcrumb">
                <RouterLink to="/">RecordSync &amp; Consent</RouterLink>
            </Title>
            <Title :type="TitleType.PageTitle">
                Record List
            </Title>
        </template>
        <template #right>
            <ChoiceButton @click="compData.showSharingHistoryDialog = true" :displayStyle="ButtonDisplayStyle.TextPrimary" :endingPadding="1">
                Sharing History
            </ChoiceButton>

            <ActionButtonV2 :action="ActionButtonStyle.Refresh" :performAction="refreshData">
                Refresh Records
            </ActionButtonV2>
        </template>
    </Row>
    
    <Title :type="TitleType.SectionTitle">
        Document Search has returned <b>{{documentResultCountText()}}.</b>
    </Title>
    <Title :type="TitleType.SectionSubTitle">
        <b>As of:</b> {{ prettyDateTime(compData.cachedDocumentsDateUTC) }}
    </Title>

    <div class="row">
        <div class="col-sm-4">
            <Title :type="TitleType.LabelMd">Filter By</Title>
            <TextInput v-model:textValue="compData.filterText">
                <template #BeforeLeftAddon>
                    <DropDownButton :endingPadding="0">
                        <template #button>
                            <span>
                                {{compData.filterBy}}
                                <Icon :type="IconTypes.ChveronDown" :startingPadding="1"/>
                            </span>
                        </template>
                        <template v-for="[key, value] in Object.entries(FilterListBy)">
                            <DropDownButtonItem @click="onClickFilterByDropDownItem" :data="key">{{key}}</DropDownButtonItem>
                        </template>
                    </DropDownButton>
                </template>
            </TextInput>
            <Title v-if="compData.documents?.length != visibleAndSortedDocumentItems?.length">
                <MessageBadge>
                    {{ visibleAndSortedDocumentItems?.length ?? 0 }} Matches
                </MessageBadge>
            </Title>
        </div> 
        <div class="col-sm-3">
            <Title :type="TitleType.LabelMd">Encounter</Title>
            <DatePickerNoTZ v-model:value="compData.encounterFilterStartDate" />
            <Title :type="TitleType.LabelSm">Start Date</Title>
        </div> 
        <div class="col-sm-3">
            <Title :type="TitleType.LabelMd">&nbsp;</Title>
            <DatePickerNoTZ v-model:value="compData.encounterFilterEndDate" />
            <Title :type="TitleType.LabelSm">End Date</Title>
        </div> 
        <div class="col-sm-2">
            <Title :type="TitleType.LabelMd">Sort By</Title>
            <DropDownButton :displayStyle="ExtraButtonDisplayStyle.OutlineGrey" :endingPadding="1">
                <template #button>
                    {{ SortByNames[compData.sortBy ?? SortListBy.NewestFirst] }}
                    <Icon :type="IconTypes.ChveronDown" :startingPadding="1" />
                </template>
                <template v-for="sortByVal of Object.entries(SortByNames).map(kvp => (+kvp[0]) as SortListBy)">
                    <DropDownButtonItem @click="() => onClickSortDropDownItem(sortByVal)" :data="sortByVal">{{SortByNames[<SortListBy>sortByVal]}}</DropDownButtonItem>
                </template>
            </DropDownButton>
            <ChoiceButton v-if="showResetFilterButton" :action="ActionButtonStyle.Erase" text=" " title="Reset Filters" @click="resetFilters" />
        </div> 
    </div>

    <!-- Document Search Results -->
    <div v-if="compData.documents">

        <Row :topPadding="2" :bottomPadding="2">
            <Checkbox v-model="compData.selectAllRecords" @update:modelValue="onChangeSelectAllRecords" /> {{ selectAllRecordsText }}
            <ChoiceButton :size="ButtonDisplaySize.Small" @click="showAllDocumentDetails(false)">Hide All Details</ChoiceButton>
            <ChoiceButton :size="ButtonDisplaySize.Small" @click="showAllDocumentDetails(true)">Show All Details</ChoiceButton>
        </Row>

        <AlertSection v-if="selectedVisibleDocuments.length > 1">
            <Row>
                <div style="line-height: 2rem; text-align: middle">
                    {{ selectedVisibleDocuments.length }} items selected
                </div>
                <template #right>
                    <div style="line-height: 2rem; text-align: middle">
                        <ActionButtonV2 :action="ActionButtonStyle.Refresh" :performAction="importSelected" :displayStyle="ButtonDisplayStyle.TextPrimary" :endingPadding="1">
                            Import Selected
                        </ActionButtonV2>

                        <VerticalSpacer/>

                        <ChoiceButton @click="cancelImportSelections" :displayStyle="ButtonDisplayStyle.TextPrimary" :marginStart="1">
                            Cancel
                        </ChoiceButton>
                    </div>
                </template>
            </Row>
        </AlertSection>

        <template v-for="doc in visibleAndSortedDocumentItems" :key="doc.url">
            <div class="row mb-1">
                <div class="col-auto">
                    <Checkbox v-model="doc.selected" />
                </div>
                <div class="col">
                    <Card :cssFramework="CssFramework.EverHealth" :showDetails="!(doc.hideCardDetails ?? false)">
                        <template #title>
                            <Row :bottomPadding="2" :topPadding="2">
                                <template #left>
                                    <ToggleIcon :folded="doc.hideCardDetails ?? false" @update:folded="val => doc.hideCardDetails = val" :title="doc.hideCardDetails ? 'Show document details' : 'Hide document details'" />
                                    {{ documentTitle(doc) }}
                                </template>
                                <template #right>
                                    <RetrieveStatusBadge :doc="doc" :title="doc.saveErrorMsg"/>
                                    <Icon :icon="IconTypes.View" title="View" @click="onViewDocument(doc)" :startingPadding="2" :hoverCursor="HoverCursorTypes.Pointer"/>
                                    <Icon v-if="!isCCDDocument(doc)" :icon="IconTypes.Download" title="Import as EDocument" @click="onClickImportDocument(doc)" :startingPadding="2" :hoverCursor="HoverCursorTypes.Pointer" />
                                    <Icon v-if="isCCDDocument(doc)" :icon="IconTypes.Download" title="Import CCD" @click="onClickImportDocument(doc)" :startingPadding="2" :hoverCursor="HoverCursorTypes.Pointer" />
                                </template>
                            </Row>
                        </template>
                        
                        <div class="row">
                            <div class="col-sm-4">
                                <Title :type="TitleType.BoldLabel">
                                    Type
                                </Title>
                                {{ doc.type?.displayName?.trim() }} <span :title="doc.type?.system" v-if="doc.type">({{ doc.type?.code}})</span>
                                <Icon :icon="IconTypes.Filter" 
                                    title="Filter by this Type"
                                    @click="setFilter(FilterListBy.Type, doc.type?.displayName)" 
                                    :hoverCursor="HoverCursorTypes.Pointer" 
                                    :startingPadding="1"
                                    v-if="doc.type?.displayName && !matchesFilter(FilterListBy.Type, doc.type?.displayName) && doc.type?.displayName?.trim()?.length"/>
                            </div>
                            <div class="col-sm-4">
                                <Title :type="TitleType.BoldLabel">
                                    Source
                                </Title>                    
                                <span>{{ doc.source?.trim() }}</span>
                                <Icon :icon="IconTypes.Filter" 
                                    title="Filter by this Source"
                                    @click="setFilter(FilterListBy.Source, doc.source)" 
                                    :hoverCursor="HoverCursorTypes.Pointer" 
                                    :startingPadding="1"
                                    v-if="doc.type?.displayName && !matchesFilter(FilterListBy.Source, doc.source) && doc.source?.trim()?.length"/>
                            </div>
                            <div class="col-sm-4">
                                <Title :type="TitleType.BoldLabel">
                                    Encounter
                                </Title>
                                <div title="Encounter Start Date/Time">
                                    {{  prettyDateTime(doc.startTimestampUTC) }}
                                </div>
                                <div title="Encounter End Date/Time">
                                    {{  prettyDateTime(doc.endTimestampUTC) }}
                                </div>
                            </div>
                        </div>
        
                        <div class="row mt-2">
                            <div class="col-sm-4">
                                <Title :type="TitleType.BoldLabel">
                                    Category
                                </Title>
                                {{ doc.category?.displayName?.trim() }} <span :title="doc.category?.system" v-if="doc.category">({{ doc.category?.code}})</span>
                                <Icon :icon="IconTypes.Filter" 
                                        title="Filter by this Category"
                                        @click="setFilter(FilterListBy.Category, doc.category?.displayName)" 
                                        :hoverCursor="HoverCursorTypes.Pointer" 
                                        :startingPadding="1"
                                        v-if="doc.category?.displayName && !matchesFilter(FilterListBy.Category, doc.category?.displayName) && doc.category?.displayName?.trim()?.length" />
                            </div>
                            <div class="col-sm-4">
                                <Title :type="TitleType.BoldLabel">
                                    Author
                                </Title>
                                <span>{{ doc.author?.trim() }}</span>
                                <Icon :icon="IconTypes.Filter" 
                                    title="Filter by this Author"
                                    @click="setFilter(FilterListBy.Author, doc.author)" 
                                    :hoverCursor="HoverCursorTypes.Pointer" 
                                    :startingPadding="1"
                                    v-if="doc.type?.displayName && !matchesFilter(FilterListBy.Author, doc.author) && doc.author?.trim()?.length"/>
                            </div>
                            <div class="col-sm-4" v-if="isDevEnv">
                                <Title :type="TitleType.BoldLabel" :displayStyle="TextDisplayStyle.Muted">
                                    Content Type
                                </Title>
                                <MessageText :displayStyle="TextDisplayStyle.Muted">
                                    {{ doc.contentType }}
                                </MessageText>
                                
                            </div>

                        </div>
                    </Card>
                </div>
            </div>
        </template>

        <template v-if="!visibleAndSortedDocumentItems?.length">
            <Row>
                <template #center>
                    <Title :type="TitleType.HeadingSm"></Title>
                </template>
            </Row>
            <Row>
                <template #center>
                    <Title :type="TitleType.BodyLg"></Title>
                </template>
            </Row>            
        </template>
        <br/>
    </div>

    <GeneralDialog v-model:show="compData.showEDocumentSaveScreen" @hidden="() => closeEDocumentsSaveScreen()">
        <template #title>Save As EDocument</template>
        <div>
            <MessageText :displayStyle="TextDisplayStyle.Muted">
                {{ documentTitle(compData.documentToImport?.doc) }}
            </MessageText>
        </div>
        <EDocumentSaveScreen ref="saveEDocsRef" 
                :visibleMenuTypes="[MenuNameType.Patient]" 
                :hidePatientSearch="true"
                :filename="docFileNameWithoutExtension(compData.documentToImport?.doc, compData.documentToImport?.fileExt)" 
                :filenameExt="compData.documentToImport?.fileExt"
                :allowManualExtEntry="!compData.documentToImport?.fileExt">
        </EDocumentSaveScreen>
        <template #footer>
            <ActionButtonV2 :action="ActionButtonStyle.Save" :performAction="async() => await completeImportDocument()">Save</ActionButtonV2>
        </template>
     </GeneralDialog>

    <GeneralDialog v-model:show="compData.showDocumentViewer">
        <!-- Set minimum dialog width -->
        <div style="height: 0px; width: 75vw;"></div>
        <template #title>
            {{ documentTitle(compData.documentToView) }}
            <Icon v-if="!compData.loadingDocumentToView && compData.documentToView && !getImportErrorMsg(compData.documentToView)"
                    :icon="IconTypes.Download" 
                    title="Import into OfficeMD" 
                    @click="onClickImportDocument(compData.documentToView)" 
                    :startingPadding="2" 
                    :hoverCursor="HoverCursorTypes.Pointer" />
        </template>
        <DocumentViewer 
            v-if="compData.documentToView" 
            :documentToView="compData.documentToView" 
            :patientId="props.patientId"
            @documentLoaded="documentViewerLoadComplete"
            @errorLoadingDocument="errorLoadingDocument" />
    </GeneralDialog>

    <SharingHistoryDialog v-model:show="compData.showSharingHistoryDialog" :patientId="props.patientId" />
 </template>